import { Suspense, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { Search } from "lucide-react";
import { usePermissions } from "@/lib/permissions";
import type { Permission } from "@/lib/rbac";
import { prefetchRouteChunk } from "@/lib/routePrefetch";
import { SoftwareUpdateDot } from "@/pages/SoftwareUpdates";
import { cn } from "@/lib/cn";

/**
 * §settings-redesign (2026-06-03) — Settings now uses ONE left sidebar, grouped
 * Workspace / Team / Account, each item opening its own focused page on the
 * right. This replaces the previous double-navigation (top tab strip + an inner
 * numbered scroll-spy rail), which is the nested-navigation antipattern NN/g and
 * others warn against. Vertical on desktop, a horizontal scroll strip on mobile.
 *
 * Permission model unchanged: items the operator can't access are hidden; the
 * /settings index (SettingsHome in App.tsx) forwards to the first permitted page.
 * Security + What's new are universal (no gate).
 */

type Item = {
  to: string;
  label: string;
  permission: Permission | null;
  dot?: boolean;
  /** §settings-ia-v2 — search synonyms: what an operator might TYPE when
   * hunting for a setting that lives on this page. */
  keywords?: string;
};
type Group = { label: string; items: Item[] };

// §settings-ia-v2 (2026-07-11) — IA fixes over the June redesign:
//  • "Security" appeared TWICE (Workspace + Account) and both pointed at the
//    same URL — which itself was double-registered, leaving the real 2FA /
//    password page unreachable. Workspace's entry is now honestly named
//    "Agent guardrails" (that's what it configures); Account's goes to its
//    own /settings/account-security route.
//  • "Payments" vs "Billing" needed prose to disambiguate → "Customer
//    payments" (what customers pay you) vs "Plan & billing" (what you pay).
//  • "Email sending" restored — fully built, orphaned from the nav in June.
//  • A search box filters the rail (Stripe/Notion/Slack settings pattern).
const GROUPS: Group[] = [
  {
    label: "Workspace",
    items: [
      { to: "/settings/business", label: "Business profile", permission: "settings.view", keywords: "business name brand agent name rename company identity logo" },
      { to: "/settings/channels", label: "Channels", permission: "settings.view", keywords: "telegram whatsapp instagram bot token webhook meta messaging" },
      { to: "/settings/phone", label: "Phone & voice", permission: "settings.view", keywords: "calls sip voice widget number ucm grandstream twilio did" },
      { to: "/settings/notifications", label: "Notifications", permission: "settings.view", keywords: "push alerts browser device" },
      { to: "/settings/email", label: "Email sending", permission: "settings.view", keywords: "gmail smtp app password invoices sender mailbox" },
      { to: "/settings/marketing", label: "Marketing", permission: "settings.view", keywords: "esp templates campaigns broadcast resend domain" },
      { to: "/settings/payments", label: "Customer payments", permission: "settings.view", keywords: "stripe checkout payment links card cod paid" },
      { to: "/settings/providers", label: "AI providers", permission: "settings.view", keywords: "openai grok xai gemini api key model brain" },
      { to: "/settings/security", label: "Agent guardrails", permission: "settings.view", keywords: "security money large orders refunds payment link lock limits" },
      { to: "/settings/billing", label: "Plan & billing", permission: "settings.view", keywords: "subscription invoice upgrade plan orikin xpand card portal" },
    ],
  },
  {
    label: "Team",
    items: [
      { to: "/settings/members", label: "Members", permission: "operators.view", keywords: "operators users invite team seat" },
      { to: "/settings/roles", label: "Roles & permissions", permission: "roles.view", keywords: "rbac access level admin viewer manage" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/settings/account-security", label: "Security & sign-in", permission: null, keywords: "2fa totp mfa password authenticator sign-in activity sessions" },
      { to: "/settings/whats-new", label: "What's new", permission: null, dot: true, keywords: "release notes version updates changelog" },
    ],
  },
];

export function SettingsLayout() {
  const { has, loading } = usePermissions();
  const navigate = useNavigate();
  const visible = (it: Item) => loading || it.permission === null || has(it.permission);
  // §settings-ia-v2 — rail search: type what you're hunting for ("2fa",
  // "stripe", "webhook") and the rail collapses to the pages that carry it.
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const matches = (it: Item) =>
    !needle ||
    it.label.toLowerCase().includes(needle) ||
    (it.keywords ?? "").toLowerCase().includes(needle);
  const firstHit = useMemo(() => {
    if (!needle) return null;
    for (const g of GROUPS) {
      const hit = g.items.find((it) => visible(it) && matches(it));
      if (hit) return hit;
    }
    return null;
  }, [needle, loading]);

  return (
    <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
      <nav
        aria-label="Settings"
        className="shrink-0 overflow-x-auto px-3 py-3 sm:px-4 lg:w-[248px] lg:overflow-y-auto lg:border-r lg:border-glass-hairline lg:px-5 lg:py-[26px]"
      >
        <div className="mb-3 hidden font-display text-g-h3 font-semibold text-fg-1 lg:block">
          Settings
        </div>
        <div
          className="mb-4 hidden h-8 items-center rounded-g-md border border-glass-hairline px-2.5 lg:flex"
          style={{ background: "var(--glass-3)", boxShadow: "var(--shadow-inset-well)" }}
        >
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-fg-4" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && firstHit) {
                navigate(firstHit.to);
                setQ("");
              } else if (e.key === "Escape") {
                setQ("");
              }
            }}
            placeholder="Search settings…"
            aria-label="Search settings"
            className="w-full bg-transparent font-glass text-g-caption text-fg-1 outline-none placeholder:text-fg-4"
          />
        </div>
        <div className="flex gap-1.5 lg:flex-col lg:gap-0">
          {GROUPS.map((g) => {
            const items = g.items.filter(visible).filter(matches);
            if (items.length === 0) return null;
            return (
              <div key={g.label} className="contents lg:block">
                <div className="xpand-overline hidden px-3 pb-2 pt-[18px] lg:block">
                  {g.label}
                </div>
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onMouseEnter={() => prefetchRouteChunk(it.to)}
                    onFocus={() => prefetchRouteChunk(it.to)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 whitespace-nowrap rounded-g-md px-3 py-2 font-glass text-g-label transition-[background,color,box-shadow] duration-g-fast ease-stand-glass",
                        isActive
                          ? "bg-glass-2 text-fg-1 shadow-rim"
                          : "text-fg-3 hover:bg-glass-3 hover:text-fg-1",
                      )
                    }
                  >
                    {it.label}
                    {it.dot ? (
                      <span className="ml-auto inline-flex items-center">
                        <SoftwareUpdateDot />
                      </span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>
      </nav>

      <div className="flex min-h-0 flex-1 flex-col">
        <Suspense fallback={<div aria-busy="true" className="flex-1" />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
}

export default SettingsLayout;
